import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Heart, Users, Shield, MessageCircle, MapPin } from 'lucide-react'

const HomePage = () => {
  const { t } = useTranslation()

  const services = [
    {
      icon: Heart,
      titleKey: 'services.pension.title',
      descKey: 'services.pension.desc',
      color: 'bg-pink-100 text-pink-600'
    },
    {
      icon: Users,
      titleKey: 'services.training.title', 
      descKey: 'services.training.desc',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: MapPin,
      titleKey: 'services.walk.title',
      descKey: 'services.walk.desc', 
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: Users,
      titleKey: 'services.walkTogether.title',
      descKey: 'services.walkTogether.desc',
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      icon: Heart,
      titleKey: 'services.mate.title',
      descKey: 'services.mate.desc',
      color: 'bg-purple-100 text-purple-600'
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero section */}
      <section className="bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-600 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-8 tracking-tight">
            {t('home.hero.title')}
          </h1>
          <p className="text-2xl md:text-3xl text-gray-700 dark:text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            {t('home.hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link
              to="/search"
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-10 py-4 rounded-3xl text-xl font-semibold inline-flex items-center space-x-3 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <Search size={24} />
              <span>{t('home.hero.searchCTA')}</span>
            </Link>
            <Link
              to="/auth"
              className="bg-white dark:bg-gray-700 hover:bg-orange-50 dark:hover:bg-gray-600 text-gray-900 dark:text-white border-2 border-orange-200 dark:border-gray-500 hover:border-orange-300 dark:hover:border-gray-400 px-10 py-4 rounded-3xl text-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {t('home.hero.joinCTA')}
            </Link>
          </div>
        </div>
      </section>

      {/* Services section */}
      <section className="pt-8 pb-24 bg-gradient-to-br from-white to-orange-25 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <img 
              src="/logo_tiering.png" 
              alt="Tiering.de Logo" 
              className="w-1/4 max-w-xs mx-auto mb-8"
            />
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight">
              {t('home.services.title')}
            </h2>
            <p className="text-2xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {t('home.services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {services.map((service, index) => {
              const Icon = service.icon
              return (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 p-10 rounded-3xl shadow-xl border border-orange-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-3"
                >
                  <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-orange-400 to-amber-400 text-white flex items-center justify-center mx-auto mb-6 shadow-xl">
                    <Icon size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {t(service.titleKey)}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
                    {t(service.descKey)}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* How it works section */}
      <section className="py-24 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-gray-700 dark:to-gray-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight">
              {t('home.howItWorks.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-amber-400 text-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Users size={40} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('home.howItWorks.step1.title')}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                {t('home.howItWorks.step1.desc')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-500 text-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Search size={40} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('home.howItWorks.step2.title')}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                {t('home.howItWorks.step2.desc')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <MessageCircle size={40} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('home.howItWorks.step3.title')}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                {t('home.howItWorks.step3.desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust section */}
      <section className="py-24 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight">
              {t('home.trust.title')}
            </h2>
            <p className="text-2xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {t('home.trust.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-amber-400 text-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('home.trust.verified.title')}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                {t('home.trust.verified.desc')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-amber-400 text-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('home.trust.community.title')}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                {t('home.trust.community.desc')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-amber-400 text-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Heart className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('home.trust.care.title')}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                {t('home.trust.care.desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section className="py-24 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-400">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 tracking-tight">
            {t('home.cta.title')}
          </h2>
          <p className="text-2xl text-orange-100 mb-12 leading-relaxed">
            {t('home.cta.subtitle')}
          </p>
          <Link
            to="/auth"
            className="bg-white hover:bg-orange-50 text-orange-600 px-12 py-5 rounded-3xl text-xl font-bold inline-block shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
          >
            {t('home.cta.button')}
          </Link>
        </div>
      </section>
    </div>
  )
}

export default HomePage